import { useState } from 'preact/hooks';
import { rpx } from 'client/lib/rpx-client';
import { showError } from '@components/app-error';
import { WeeklySnapshot } from './types';

type Props = {
  snapshots: WeeklySnapshot[];
  courseId?: string;
};

function formatWeekLabel(dateStr: string) {
  const d = new Date(dateStr.slice(0, 10) + 'T00:00:00');
  const end = new Date(d);
  end.setDate(end.getDate() + 6);
  const fmt = (dt: Date) => dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${fmt(d)} - ${fmt(end)}`;
}

function formatPct(v: number | null | undefined) {
  if (v == null || !Number.isFinite(v)) {
    return '—';
  }
  return `${v.toFixed(1)}%`;
}

function NoteCell({ weekStart, initialNote }: { weekStart: string; initialNote: string }) {
  const [note, setNote] = useState(initialNote);
  const [isEditing, setIsEditing] = useState(false);

  async function handleBlur() {
    setIsEditing(false);
    if (note !== initialNote) {
      try {
        await rpx.communityDashboard.updateWeeklyNote({ weekStart, notes: note });
      } catch (err) {
        showError(err);
      }
    }
  }

  if (isEditing) {
    return (
      <input
        type="text"
        class="w-full text-xs border rounded px-1.5 py-1"
        value={note}
        onInput={(e) => setNote((e.target as HTMLInputElement).value)}
        onBlur={handleBlur}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            (e.target as HTMLInputElement).blur();
          }
        }}
        autoFocus
      />
    );
  }

  return (
    <button
      type="button"
      class="text-left text-xs text-gray-500 hover:text-gray-700 w-full truncate"
      onClick={() => setIsEditing(true)}
      title={note || 'Click to add a note'}
    >
      {note || '—'}
    </button>
  );
}

function buildWauCsvUrl(courseId?: string, weekStart?: string) {
  const params = new URLSearchParams({ segment: 'wau', periodDays: '7' });
  if (courseId) {
    params.set('courseId', courseId);
  }
  if (weekStart) {
    params.set('weekStart', weekStart);
  }
  return `/csv/engagement-members.csv?${params.toString()}`;
}

function buildVisitorsCsvUrl(weekStart?: string) {
  const params = new URLSearchParams({ segment: 'visitors', periodDays: '7' });
  if (weekStart) {
    params.set('weekStart', weekStart);
  }
  return `/csv/engagement-members.csv?${params.toString()}`;
}

export function WeeklyTrend({ snapshots, courseId }: Props) {
  return (
    <div class="mb-8">
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-lg font-semibold text-gray-900">Weekly Trend</h3>
        <span class="text-xs text-gray-400">
          Download per-week Visitor and WAU lists via the CSV links below
        </span>
      </div>
      <div class="overflow-x-auto">
        <table class="w-full table-auto text-sm">
          <thead>
            <tr class="border-b text-left text-gray-500 text-xs">
              <th class="pb-2 pr-4 font-medium">Week</th>
              <th class="pb-2 pr-4 font-medium text-right">Members</th>
              <th class="pb-2 pr-4 font-medium text-right">Visitors</th>
              <th class="pb-2 pr-4 font-medium text-right">WAU</th>
              <th class="pb-2 pr-4 font-medium text-right">Eng. Rate</th>
              <th class="pb-2 pr-4 font-medium text-right">Posts</th>
              <th class="pb-2 pr-4 font-medium text-right">Replies</th>
              <th class="pb-2 pr-4 font-medium text-right">Likes</th>
              <th
                class="pb-2 pr-4 font-medium text-right"
                title="Unique AIP members who joined the Tuesday live call (via bridge page or in-app Join)"
              >
                Tue Call
              </th>
              <th
                class="pb-2 pr-4 font-medium text-right"
                title="Unique AIP members who joined the Thursday live call"
              >
                Thu Call
              </th>
              <th class="pb-2 font-medium" style="min-width: 140px">
                Notes
              </th>
              <th class="pb-2 font-medium text-right">Visitors CSV</th>
              <th class="pb-2 font-medium text-right">WAU CSV</th>
            </tr>
          </thead>
          <tbody>
            {snapshots.map((s, idx) => (
              <tr
                key={s.weekStart}
                class={`border-b ${idx === 0 ? 'bg-indigo-50/50' : 'hover:bg-gray-50'}`}
              >
                <td class="py-2 pr-4 text-xs text-gray-700 whitespace-nowrap">
                  <div class="flex flex-col gap-0.5">
                    <span>{formatWeekLabel(s.weekStart)}</span>
                    {idx === 0 && (
                      <span class="inline-block w-fit rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-700">
                        Live
                      </span>
                    )}
                  </div>
                </td>
                <td class="py-2 pr-4 text-right tabular-nums">
                  {s.totalMembers}
                  {(s.newMembers > 0 || s.cancelledMembers > 0) && (
                    <span class="text-xs text-gray-400 ml-1">
                      ({s.newMembers > 0 ? `+${s.newMembers}` : ''}
                      {s.newMembers > 0 && s.cancelledMembers > 0 ? '/' : ''}
                      {s.cancelledMembers > 0 ? `-${s.cancelledMembers}` : ''})
                    </span>
                  )}
                </td>
                <td class="py-2 pr-4 text-right tabular-nums">
                  {s.visitors ?? <span class="text-gray-300">—</span>}
                </td>
                <td class="py-2 pr-4 text-right tabular-nums">{s.wau}</td>
                <td class="py-2 pr-4 text-right tabular-nums">{formatPct(s.engagementRate)}</td>
                <td class="py-2 pr-4 text-right tabular-nums">{s.postCount}</td>
                <td class="py-2 pr-4 text-right tabular-nums">{s.replyCount}</td>
                <td class="py-2 pr-4 text-right tabular-nums">{s.likeCount}</td>
                <td class="py-2 pr-4 text-right tabular-nums">
                  {s.tueCallAttendees ?? <span class="text-gray-300">—</span>}
                </td>
                <td class="py-2 pr-4 text-right tabular-nums">
                  {s.thuCallAttendees ?? <span class="text-gray-300">—</span>}
                </td>
                <td class="py-2">
                  <NoteCell weekStart={s.weekStart.slice(0, 10)} initialNote={s.notes || ''} />
                </td>
                <td class="py-2 text-right">
                  {s.visitors == null ? (
                    <span class="text-xs text-gray-300">—</span>
                  ) : (
                    <a
                      href={buildVisitorsCsvUrl(s.weekStart.slice(0, 10))}
                      class="text-xs text-indigo-600 hover:text-indigo-800"
                      download
                    >
                      CSV
                    </a>
                  )}
                </td>
                <td class="py-2 text-right">
                  <a
                    href={buildWauCsvUrl(courseId, s.weekStart.slice(0, 10))}
                    class="text-xs text-indigo-600 hover:text-indigo-800"
                    download
                  >
                    CSV
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {snapshots.length === 0 && (
          <p class="text-gray-400 text-sm text-center py-6">
            No historical data yet. Weekly snapshots will appear here after the first Monday.
          </p>
        )}
      </div>
    </div>
  );
}
